// DayView.js - Dagsvy med behandlare som kolumner

import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAppointments, getAppointmentTypes, cancelAppointment, endSeries, occurrenceDateOf, getCalendarViews, getClinicUsers, getBlocks, deleteBlock, getScheduleBlocks, updateAppointment, updateBlock, getEffectiveSlots, createSlotOverride, deleteSlotOverride, registerNoShow, ensureRealAppointment, appointmentKey, isSeriesAppointment, isVirtualOccurrence, moveOccurrence } from './calendarApi';
import { copyAppointment, getCopiedAppointment, copiedLabel, pasteAppointment } from './calendarClipboard';
import CalendarLegend from './CalendarLegend';
import { UserContext } from '../../UserContext';
import apiClient from '../../apiClient';
import AppointmentModal from './AppointmentModal';
import SlotOverrideModal from './SlotOverrideModal';
import useContextMenuPosition from './useContextMenuPosition';
import { uteblivetBekraftelseText } from './uteblivetApi';
import { useActivePatient } from '../../patient/ActivePatientContext';
import PaymentModal from './PaymentModal';
import KallelseModal from './KallelseModal';
import BlockNoteModal from './BlockNoteModal';
import GroupParticipantsModal from './GroupParticipantsModal';
import AvailableSlotSearch from './AvailableSlotSearch';
import CancelledMarker, { groupCancelledByStart, applyCancelledGutter } from './CancelledMarker';
import './DayView.css';
import { textColorForBackground } from './colorUtils';
import { bookedByText } from './bookedBy';
import DateJumpPicker from './DateJumpPicker';

const WEEKDAYS = ['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'];
const START_HOUR = 7;
const END_HOUR = 19;
// Vertikal skala (Spår 1, 2026-09-19): ENDA sanningskällan för minut→px.
// Allt som positionerar i rutnätet (slothöjd, bokningar, block, avbokad-markör,
// schemaremsor, nu-linje) multiplicerar minuter med denna; drag delar med den.
const PIXELS_PER_MINUTE = 2;

// Drag och ändrad längd snappar alltid i 5-minuterssteg, oberoende av vald
// visningsupplösning (Spår S1). Visningen styr bara rutnätets linjer.
const DRAG_SNAP_MINUTES = 5;

// Kompakt dagvy (Spår S3) minns per användare
const compactStorageKeyFor = (userId) => `aion.dayview.kompakt.${userId}`;

// Unik nyckel per block-tillfälle: återkommande block delar seriens block_id
const blockKey = (block) => `${block.block_id}@${block.start_time}`;

// Minuter på dygnet → "HH:MM"
const formatMinutes = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

// Generera tidsslots baserat på intervall (i minuter)
const generateTimeSlots = (intervalMinutes) => {
  const slots = [];
  for (let hour = START_HOUR; hour < END_HOUR; hour++) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      slots.push({ hour, minute });
    }
  }
  return slots;
};

// Formatera tid för visning
const formatSlotTime = (slot) => {
  return `${String(slot.hour).padStart(2, '0')}:${String(slot.minute).padStart(2, '0')}`;
};

// Hjälpfunktion för lokal datumformatering
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DayView = ({ onViewChange }) => {
  const navigate = useNavigate();
  const { user: currentUser } = useContext(UserContext);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Behandlare
  // Användare med kalender + ev. användare utan kalender som ändå har bokningar
  // denna dag (has_calendar=false → nedtonad kolumn, inga nya bokningar)
  const [clinicUsers, setClinicUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]); // Array av valda behandlare
  const usersInitializedRef = useRef(false);
  const loadSeqRef = useRef(0);

  // Sparade behandlarvyer (Spår K2): klinik-admin skapar dem i
  // Kalenderinställningar. Senaste valda vy minns per användare.
  const [calendarViews, setCalendarViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState('');
  const viewRestoredRef = useRef(false);
  const viewStorageKey = currentUser ? `aion.dayview.vy.${currentUser.user_id}` : null;

  // Blocks och schema
  const [blocks, setBlocks] = useState([]);
  const [scheduleBlocks, setScheduleBlocks] = useState([]);

  // Schemamall-slots för dagen per behandlare: { userId: [slot, ...] }
  const [scheduleSlots, setScheduleSlots] = useState({});
  const [activeSlotInfo, setActiveSlotInfo] = useState(null); // { x, y, slot }
  const [preSelectedSlotType, setPreSelectedSlotType] = useState(null);
  const [slotModal, setSlotModal] = useState(null); // { mode, date, userId, slot }

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedUserIdForBooking, setSelectedUserIdForBooking] = useState('');

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAppointment, setPaymentAppointment] = useState(null);

  // Kallelse modal state
  const [showKallelseModal, setShowKallelseModal] = useState(false);
  const [kallelseAppointment, setKallelseAppointment] = useState(null);
  const [preSelectedKallelseTemplate, setPreSelectedKallelseTemplate] = useState(null);

  // Block/Note modal state
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockModalMode, setBlockModalMode] = useState('block');
  const [selectedBlock, setSelectedBlock] = useState(null);

  // Group participants modal state
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupAppointment, setGroupAppointment] = useState(null);

  // Available slot search modal state
  const [showSlotSearch, setShowSlotSearch] = useState(false);
  // Aktiv patient (Spår 3): högerklick "Boka ny tid" gör bokningens patient aktiv
  // så AppointmentModal förväljer den när en ledig tid valts.
  const { setActivePatient } = useActivePatient() || {};

  // Klinik-inställningar för journalmodul
  const [useInternalJournal, setUseInternalJournal] = useState(false);

  // Tidsintervall för slots (minuter): 60, 30, 15, eller egen
  const [slotInterval, setSlotInterval] = useState(30);

  // Generera tidsslots baserat på intervall
  const timeSlots = useMemo(() => generateTimeSlots(slotInterval), [slotInterval]);

  // Beräkna slothöjd baserat på intervall och vertikal skala
  const slotHeight = slotInterval * PIXELS_PER_MINUTE;

  // Kompakt dagvy (Spår S3): alla behandlare får plats i bredd utan sidscroll.
  // Valet minns per användare i webbläsaren; avstängt = som tidigare.
  const [compact, setCompact] = useState(false);
  const currentUserId = currentUser?.user_id;
  useEffect(() => {
    if (currentUserId == null) return;
    try {
      setCompact(localStorage.getItem(compactStorageKeyFor(currentUserId)) === '1');
    } catch (e) { /* ignoreras */ }
  }, [currentUserId]);
  const toggleCompact = () => {
    const next = !compact;
    setCompact(next);
    if (currentUserId == null) return;
    try {
      if (next) localStorage.setItem(compactStorageKeyFor(currentUserId), '1');
      else localStorage.removeItem(compactStorageKeyFor(currentUserId));
    } catch (e) { /* ignoreras */ }
  };

  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    appointment: null,
    block: null,
    slotDate: null,
    userId: null
  });

  // Ref + klampad position så högerklicksmenyn aldrig klipps av mot skärmkanten (Spår B)
  const contextMenuRef = useRef(null);
  const contextMenuPos = useContextMenuPosition(
    contextMenuRef, contextMenu.visible, contextMenu.x, contextMenu.y
  );

  // Markerad bokning (enkelklick markerar, dubbelklick öppnar)
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);

  // Nu-linje (Spår 6): aktuell tid, tickar en gång per minut
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);
  const nowTop = (now.getHours() * 60 + now.getMinutes()) - START_HOUR * 60;
  const nowVisible = nowTop >= 0 && nowTop <= (END_HOUR - START_HOUR) * 60;
  const nowTopPx = nowTop * PIXELS_PER_MINUTE;

  // Drag and drop state
  const [dragState, setDragState] = useState({
    isDragging: false,
    itemType: null, // 'appointment' eller 'block'
    itemId: null,
    itemData: null,
    mode: 'move', // 'move' eller 'resize' (ändra längd, Spår K3)
    startY: 0,
    currentY: 0,
    startX: 0,
    currentX: 0,
    originalTop: 0,
    containerTop: 0
  });
  const dragRef = useRef(null);
  const justDraggedRef = useRef(false);

  // Ladda användare för kliniken - bara de med kalender, plus de utan kalender
  // som har bokningar den visade dagen (så att bokningarna inte blir osynliga)
  useEffect(() => {
    let stale = false; // snabb bläddring: ett äldre svar får inte skriva över
    const loadUsers = async () => {
      try {
        const dayStart = new Date(currentDate);
        dayStart.setHours(0, 0, 0, 0);
        const nextDay = new Date(dayStart);
        nextDay.setDate(nextDay.getDate() + 1);
        const users = await getClinicUsers({
          calendarOnly: true,
          from: formatLocalDate(dayStart),
          to: formatLocalDate(nextDay)
        });
        if (stale) return;
        const filteredUsers = users.filter(u => u.clinic_id === currentUser?.clinic_id);
        setClinicUsers(filteredUsers);
        // Välj alla behandlare med kalender som standard (första laddningen)
        if (!usersInitializedRef.current) {
          usersInitializedRef.current = true;
          setSelectedUserIds(filteredUsers.filter(u => u.has_calendar !== false).map(u => u.user_id));
        }
      } catch (err) {
        console.error('Error loading users:', err);
      }
    };
    if (currentUser) {
      loadUsers();
    }
    return () => { stale = true; };
  }, [currentUser, currentDate]);

  // Kolumner som visas: valda behandlare med kalender + alltid de utan kalender
  // som har bokningar dagen (nedtonade). Strängnyckel så att loadData inte körs
  // om när listan laddas om med samma innehåll.
  const visibleUserKey = clinicUsers
    .filter(u => u.has_calendar === false || selectedUserIds.includes(u.user_id))
    .map(u => u.user_id)
    .join(',');

  // Hämta klinik-inställningar för journalmodul
  useEffect(() => {
    const fetchClinicSettings = async () => {
      try {
        const resp = await apiClient.get('/api/journal/clinic/settings');
        setUseInternalJournal(resp.data.use_internal_journal || false);
      } catch (error) {
        setUseInternalJournal(false);
      }
    };
    fetchClinicSettings();
  }, []);

  // Ladda bokningar och blocks för vald dag
  const loadData = useCallback(async () => {
    const visibleUserIds = visibleUserKey ? visibleUserKey.split(',').map(Number) : [];
    // Dag-/användarbyte kan ge två laddningar i följd - bara den senaste får skriva
    const seq = ++loadSeqRef.current;
    if (visibleUserIds.length === 0) {
      setAppointments([]);
      setBlocks([]);
      setScheduleBlocks([]);
      setScheduleSlots({});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const dayStart = new Date(currentDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(currentDate);
      dayEnd.setHours(23, 59, 59, 999);

      const fromDateStr = formatLocalDate(dayStart);
      const toDateStr = formatLocalDate(dayEnd);

      // Hämta data för alla valda behandlare
      const allAppointments = [];
      const allBlocks = [];
      const allScheduleBlocks = [];
      const slotsByUser = {};

      await Promise.all(visibleUserIds.map(async (userId) => {
        const [userAppointments, userBlocks, userSchedule, userSlots] = await Promise.all([
          getAppointments(dayStart.toISOString(), dayEnd.toISOString(), userId),
          getBlocks(dayStart.toISOString(), dayEnd.toISOString(), userId),
          getScheduleBlocks(userId, fromDateStr, toDateStr),
          getEffectiveSlots(userId, { date: fromDateStr })
        ]);

        allAppointments.push(...userAppointments);
        allBlocks.push(...userBlocks);
        if (userSchedule?.schedule_blocks) {
          allScheduleBlocks.push(...userSchedule.schedule_blocks.map(sb => ({ ...sb, user_id: userId })));
        }
        slotsByUser[userId] = userSlots?.slots || [];
      }));

      // Hämta appointment types en gång
      const types = await getAppointmentTypes();
      if (seq !== loadSeqRef.current) return; // nyare laddning pågår

      setAppointments(allAppointments);
      setBlocks(allBlocks);
      setScheduleBlocks(allScheduleBlocks);
      setScheduleSlots(slotsByUser);
      setAppointmentTypes(types);
    } catch (err) {
      console.error('Error loading calendar data:', err);
      if (seq === loadSeqRef.current) setError('Kunde inte ladda kalenderdata');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [currentDate, visibleUserKey]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Navigation
  const goToPreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const goToPreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Klinikens sparade vyer (Spår K2)
  useEffect(() => {
    if (!currentUser) return;
    getCalendarViews()
      .then(setCalendarViews)
      .catch(err => console.error('Kunde inte ladda sparade vyer:', err));
  }, [currentUser]);

  // Minns senast valda vy per användare (localStorage kan saknas/blockeras)
  const rememberView = useCallback((viewId) => {
    setActiveViewId(viewId ? String(viewId) : '');
    if (!viewStorageKey) return;
    try {
      if (viewId) localStorage.setItem(viewStorageKey, String(viewId));
      else localStorage.removeItem(viewStorageKey);
    } catch (e) { /* ignoreras */ }
  }, [viewStorageKey]);

  // Vy = exakt de behandlarna (konton utan kalender visas ändå bara när
  // de har bokningar dagen, se selectedUsers)
  const applyView = useCallback((view) => {
    setSelectedUserIds([...(view.user_ids || [])]);
    rememberView(view.view_id);
  }, [rememberView]);

  // Återställ senaste vy när både vyer och behandlare finns (en gång)
  useEffect(() => {
    if (viewRestoredRef.current || !calendarViews.length || !usersInitializedRef.current) return;
    viewRestoredRef.current = true;
    let storedId = null;
    try { storedId = viewStorageKey && localStorage.getItem(viewStorageKey); } catch (e) { /* ignoreras */ }
    const view = storedId && calendarViews.find(v => String(v.view_id) === storedId);
    if (view) applyView(view);
  }, [calendarViews, clinicUsers, viewStorageKey, applyView]);

  const handleViewSelect = (viewId) => {
    const view = calendarViews.find(v => String(v.view_id) === viewId);
    if (view) applyView(view);
    else rememberView('');
  };

  // Toggle behandlare - ett manuellt val lämnar den sparade vyn
  const toggleUser = (userId) => {
    rememberView('');
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const selectAllUsers = () => {
    rememberView('');
    setSelectedUserIds(clinicUsers.filter(u => u.has_calendar !== false).map(u => u.user_id));
  };

  const deselectAllUsers = () => {
    rememberView('');
    setSelectedUserIds([]);
  };

  // Klick på tom tid - avmarkera markerad bokning
  const handleSlotClick = (slot, userId) => {
    setSelectedAppointmentId(null);
  };

  // Enkelklick på bokning - endast markera
  const handleAppointmentClick = (appointment, e) => {
    e.stopPropagation();
    // Toggle selection - klicka igen för att avmarkera
    const key = appointmentKey(appointment);
    if (selectedAppointmentId === key) {
      setSelectedAppointmentId(null);
    } else {
      setSelectedAppointmentId(key);
    }
  };

  // Dubbelklick på bokning - öppna modal
  const handleAppointmentDoubleClick = (appointment, e) => {
    e.stopPropagation();
    setSelectedAppointment(appointment);
    setSelectedSlot(null);
    setSelectedUserIdForBooking(String(appointment.user_id));
    setShowModal(true);
  };

  // Dubbelklick på tom tid - öppna ny bokning.
  // Träffar tiden en schemaslot förifylls slotens typ + tid, som vid högerklick.
  // Användare utan kalender (nedtonad kolumn) tar inte emot nya bokningar
  const isCalendarDisabled = (userId) => clinicUsers.some(
    u => u.user_id === userId && u.has_calendar === false
  );

  const handleSlotDoubleClick = (slot, userId) => {
    if (isCalendarDisabled(userId)) return;
    const scheduleSlot = getSlotAtTime(userId, slot.hour, slot.minute);
    const slotDate = new Date(currentDate);
    if (scheduleSlot) {
      const [sh, sm] = scheduleSlot.start_time.split(':').map(Number);
      const [eh, em] = scheduleSlot.end_time.split(':').map(Number);
      slotDate.setHours(sh, sm, 0, 0);
      setPreSelectedSlotType({
        type_id: scheduleSlot.appointment_type_id,
        name: scheduleSlot.appointment_type?.name,
        duration_minutes: (eh * 60 + em) - (sh * 60 + sm)
      });
    } else {
      slotDate.setHours(slot.hour, slot.minute, 0, 0);
    }
    setSelectedSlot(slotDate);
    setSelectedAppointment(null);
    setSelectedUserIdForBooking(String(userId));
    setShowModal(true);
  };

  // Högerklick på tom tid - använder slotens exakta tid
  const handleSlotContextMenu = (slot, userId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isCalendarDisabled(userId)) return; // kalender avstängd

    const slotDate = new Date(currentDate);
    slotDate.setHours(slot.hour, slot.minute, 0, 0);

    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      appointment: null,
      block: null,
      slotDate: slotDate,
      userId: userId,
      scheduleSlot: getSlotAtTime(userId, slot.hour, slot.minute)
    });
  };

  // Högerklick på bokning
  const handleAppointmentContextMenu = (appointment, e) => {
    e.preventDefault();
    e.stopPropagation();

    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      appointment: appointment,
      block: null,
      slotDate: new Date(appointment.start_time), // Använd bokningens starttid
      userId: appointment.user_id
    });
  };

  // Högerklick på block/note
  const handleBlockContextMenu = (block, e) => {
    e.preventDefault();
    e.stopPropagation();

    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      appointment: null,
      block: block,
      slotDate: new Date(block.start_time),
      userId: block.user_id
    });
  };

  // Stäng kontextmeny
  const closeContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, visible: false }));
  }, []);

  // Stäng kontextmeny vid klick utanför
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) {
        closeContextMenu();
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('contextmenu', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('contextmenu', handleClickOutside);
    };
  }, [contextMenu.visible, closeContextMenu]);

  // ==================== DRAG AND DROP ====================
  // Flytt (drag i kortet) och ändrad längd (drag i nederkanten, Spår K3).
  // Båda snappar i 5-minuterssteg (DRAG_SNAP_MINUTES, Spår S1) oavsett vald
  // upplösning, och flytt bevarar längden exakt.

  // Kontrollera om en bokning kan dras (ej betald, ej genomförd)
  const canDragAppointment = (apt) => {
    return apt.status !== 'completed' && !apt.is_paid;
  };

  // Snappa en tidpunkt till närmaste 5-minuterssteg (minuter på dygnet)
  const snapToGrid = (date) => {
    const totalMinutes = date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;
    const snapped = Math.round(totalMinutes / DRAG_SNAP_MINUTES) * DRAG_SNAP_MINUTES;
    const result = new Date(date);
    result.setHours(Math.floor(snapped / 60), snapped % 60, 0, 0);
    return result;
  };

  // Beräkna ny tid baserat på Y-offset - snappar den nya STARTTIDEN
  const calculateNewTime = (originalTime, deltaY) => {
    const deltaMinutes = deltaY / PIXELS_PER_MINUTE;
    return snapToGrid(new Date(new Date(originalTime).getTime() + deltaMinutes * 60 * 1000));
  };

  // Ny sluttid vid ändrad längd: snappad, och alltid efter starten
  const calculateResizedEnd = (item, deltaY) => {
    const start = new Date(item.start_time);
    const end = new Date(item.end_time);
    let newEnd = snapToGrid(new Date(end.getTime() + (deltaY / PIXELS_PER_MINUTE) * 60000));
    if (newEnd <= start) newEnd = new Date(start.getTime() + DRAG_SNAP_MINUTES * 60000);
    return newEnd;
  };

  // Ny starttid vid flytt - samma regel som när draget släpps: ett rent
  // horisontellt drag (bara behandlarbyte) behåller exakt tid
  const calculateMovedStart = (item, deltaY) => (Math.abs(deltaY) < 5
    ? new Date(item.start_time)
    : calculateNewTime(item.start_time, deltaY));

  // Formatera datum för API
  const formatDateTimeForApi = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:00`;
  };

  // Drag start handler - sparar startposition, drag startar först vid rörelse.
  // mode 'resize' = greppet i kortets nederkant (ändra längd).
  const handleDragStart = (e, itemType, item, mode = 'move') => {
    // Endast vänster musknapp
    if (e.button !== 0) return;

    // Förhindra drag för betalda/genomförda bokningar
    if (itemType === 'appointment' && !canDragAppointment(item)) {
      return;
    }

    // Greppet ligger i kortet - låt inte kortets egen mousedown starta en flytt
    if (mode === 'resize') e.stopPropagation();

    // Spara startposition - drag startar först när musen rör sig tillräckligt
    dragRef.current = {
      element: e.currentTarget,
      itemType,
      item,
      mode,
      startY: e.clientY,
      startX: e.clientX,
      started: false
    };
  };

  // Drag move handler
  const handleDragMove = useCallback((e) => {
    if (!dragState.isDragging) return;

    setDragState(prev => ({
      ...prev,
      currentY: e.clientY,
      currentX: e.clientX
    }));
  }, [dragState.isDragging]);

  // Spara ny tid för ett block. Ett återkommande block är EN rad i databasen
  // (tillfällena är virtuella) - ändringen gäller hela serien och översätts
  // därför till seriens egen start. Returnerar false om användaren avbröt.
  const saveBlockTimes = async (block, newStart, newEnd) => {
    const isRecurringBlock = block.recurrence_pattern !== 'none' || block.is_recurring_instance;
    if (isRecurringBlock && !window.confirm('Detta är en återkommande post. Ändringen gäller hela serien. Fortsätt?')) {
      return false;
    }
    let start = newStart;
    if (block.is_recurring_instance && block.series_start_time) {
      start = new Date(new Date(block.series_start_time).getTime()
        + (newStart.getTime() - new Date(block.start_time).getTime()));
    }
    const end = new Date(start.getTime() + (newEnd.getTime() - newStart.getTime()));
    await updateBlock(block.block_id, {
      start_time: formatDateTimeForApi(start),
      end_time: formatDateTimeForApi(end)
    });
    return true;
  };

  // Ändra längd på bokning eller block (Spår K3)
  const finishResize = async (itemType, itemData, deltaY) => {
    const newEnd = calculateResizedEnd(itemData, deltaY);
    if (newEnd.getTime() === new Date(itemData.end_time).getTime()) return;

    if (itemType === 'appointment') {
      // Seriens första tillfälle (modern) bär hela serien (Spår R)
      if (isSeriesAppointment(itemData) && !itemData.parent_id && !isVirtualOccurrence(itemData)
          && !window.confirm('Detta är första tillfället i en återkommande serie. Ändra längden för hela serien?')) {
        return;
      }
      // Ett virtuellt tillfälle materialiseras med sin nya längd (samma anrop
      // som flytt - krockkollen görs innan något skapas)
      const doPut = (extra = {}) => (isVirtualOccurrence(itemData)
        ? moveOccurrence(itemData, {
          start_time: formatDateTimeForApi(new Date(itemData.start_time)),
          end_time: formatDateTimeForApi(newEnd),
          ...extra
        })
        : updateAppointment(itemData.appointment_id, { end_time: formatDateTimeForApi(newEnd), ...extra }));
      try {
        await doPut();
      } catch (err) {
        if (!err.response?.data?.requires_conflict_override) throw err;
        const msg = err.response.data.error || 'Tiden krockar med en annan bokning.';
        if (!window.confirm(`${msg}\n\nVill du dubbelboka ändå?`)) return;
        await doPut({ conflict_override: true });
      }
    } else if (!(await saveBlockTimes(itemData, new Date(itemData.start_time), newEnd))) {
      return;
    }
    await loadData();
  };

  // Drag end handler
  const handleDragEnd = useCallback(async (e) => {
    if (!dragState.isDragging) return;

    const deltaY = dragState.currentY - dragState.startY;

    // Återställ cursor
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    const { itemType, itemData, mode } = dragState;

    const resetDrag = () => {
      dragRef.current = null;
      setDragState({
        isDragging: false,
        itemType: null,
        itemId: null,
        itemData: null,
        mode: 'move',
        startY: 0,
        currentY: 0,
        startX: 0,
        currentX: 0,
        originalTop: 0,
        containerTop: 0
      });
    };

    // Markera att vi just har dragit (förhindra onClick)
    const markDragged = () => {
      justDraggedRef.current = true;
      setTimeout(() => {
        justDraggedRef.current = false;
      }, 100);
    };

    if (mode === 'resize') {
      markDragged();
      try {
        await finishResize(itemType, itemData, deltaY);
      } catch (error) {
        console.error('Fel vid ändrad längd:', error);
        alert(error.response?.data?.error || 'Kunde inte ändra längden. Försök igen.');
      }
      resetDrag();
      return;
    }

    // Spår 7: mappa släpp-X till behandlarkolumn (drag till annan behandlare).
    // Endast bokningar byter behandlare; block hålls kvar hos sin behandlare.
    let targetUserId = null;
    if (itemType === 'appointment') {
      const dropEl = document.elementFromPoint(e.clientX, e.clientY);
      const col = dropEl?.closest('.user-column');
      if (col && col.dataset.userId != null) {
        targetUserId = Number(col.dataset.userId);
      }
    }
    const userChanged = targetUserId != null && targetUserId !== itemData?.user_id;

    // Samma beräkning som förhandsvisningen under draget (Spår S1): rent
    // horisontellt drag behåller exakt tid, annars 5-minuterssnapp
    const newStartTime = calculateMovedStart(itemData, deltaY);

    // Oförändrad tid OCH ingen kolumnbyte → behandla som klick (öppna modal)
    if (newStartTime.getTime() === new Date(itemData.start_time).getTime() && !userChanged) {
      resetDrag();
      // Tillåt onClick att köras
      return;
    }

    markDragged();

    try {
      if (itemType === 'appointment') {
        const durationMs = new Date(itemData.end_time).getTime() - new Date(itemData.start_time).getTime();
        const newEndTime = new Date(newStartTime.getTime() + (durationMs > 0 ? durationMs : 30 * 60000));

        const payload = {
          start_time: formatDateTimeForApi(newStartTime),
          end_time: formatDateTimeForApi(newEndTime)
        };
        if (userChanged) payload.user_id = targetUserId;

        // Seriens första tillfälle (modern) bär hela serien - flytten gäller
        // alla tillfällen utan egna uppgifter (Spår R)
        if (isSeriesAppointment(itemData) && !itemData.parent_id && !isVirtualOccurrence(itemData)
            && !window.confirm('Detta är första tillfället i en återkommande serie. Flytta hela serien?')) {
          resetDrag(); // bokningen snappar tillbaka
          return;
        }

        // Ett virtuellt tillfälle flyttas som eget tillfälle: skapas och flyttas
        // i samma anrop, efter krockkollen - ett avbrutet drag lämnar inget
        const doPut = (extra = {}) => (isVirtualOccurrence(itemData)
          ? moveOccurrence(itemData, { ...payload, ...extra })
          : updateAppointment(itemData.appointment_id, { ...payload, ...extra }));
        try {
          await doPut();
        } catch (err) {
          // Kollisionsfråga (I2): backend nekar krock med 409 → fråga dubbelboka
          if (err.response?.data?.requires_conflict_override) {
            const msg = err.response.data.error || 'Tiden krockar med en annan bokning.';
            if (window.confirm(`${msg}\n\nVill du dubbelboka ändå?`)) {
              await doPut({ conflict_override: true });
            } else {
              resetDrag(); // avbröt → bokningen snappar tillbaka
              return;
            }
          } else {
            throw err;
          }
        }

        // Ladda om bokningar
        await loadData();

      } else if (itemType === 'block') {
        // Längden bevaras exakt - bara starten snappar (Spår K3)
        const durationMs = new Date(itemData.end_time).getTime() - new Date(itemData.start_time).getTime();
        const newEndTime = new Date(newStartTime.getTime() + durationMs);

        if (await saveBlockTimes(itemData, newStartTime, newEndTime)) {
          await loadData();
        }
      }
    } catch (error) {
      console.error('Fel vid flytt:', error);
      alert(error.response?.data?.error || 'Kunde inte flytta bokningen. Försök igen.');
    }

    // Rensa drag state
    resetDrag();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragState, loadData]);

  // Global event listeners för drag
  useEffect(() => {
    if (dragState.isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);

      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [dragState.isDragging, handleDragMove, handleDragEnd]);

  // Initial drag detection - starta drag först efter tillräcklig rörelse
  useEffect(() => {
    const DRAG_THRESHOLD = 10; // Minsta rörelse i pixlar för att starta drag
    const RESIZE_THRESHOLD = 3; // Längdgreppet är smalt - reagera tidigt

    const handleInitialMove = (e) => {
      if (!dragRef.current || dragRef.current.started) return;

      // Starta drag på rörelse i valfri riktning - horisontellt drag byter
      // behandlarkolumn (Spår 7), vertikalt byter tid. Längdgreppet bara vertikalt.
      const deltaY = Math.abs(e.clientY - dragRef.current.startY);
      const deltaX = Math.abs(e.clientX - dragRef.current.startX);
      const isResize = dragRef.current.mode === 'resize';

      if (isResize ? deltaY >= RESIZE_THRESHOLD : Math.max(deltaY, deltaX) >= DRAG_THRESHOLD) {
        // Tillräcklig rörelse - starta drag
        dragRef.current.started = true;

        const { itemType, item, mode, startY, startX, element } = dragRef.current;
        const container = element.closest('.day-slots-container');
        const containerRect = container?.getBoundingClientRect() || { top: 0 };

        document.body.style.cursor = isResize ? 'ns-resize' : 'grabbing';
        document.body.style.userSelect = 'none';

        setDragState({
          isDragging: true,
          itemType: itemType,
          // Block hade tidigare item.id (finns inte) → flytt gick mot /blocks/undefined
          itemId: itemType === 'appointment' ? appointmentKey(item) : blockKey(item),
          itemData: item,
          mode,
          startY: startY,
          currentY: e.clientY,
          startX: startX,
          currentX: e.clientX,
          originalTop: element.offsetTop,
          containerTop: containerRect.top
        });
      }
    };

    const handleInitialUp = () => {
      // Om mouseup innan drag startade, rensa dragRef och låt click-event köras
      if (dragRef.current && !dragRef.current.started) {
        dragRef.current = null;
      }
    };

    document.addEventListener('mousemove', handleInitialMove);
    document.addEventListener('mouseup', handleInitialUp);

    return () => {
      document.removeEventListener('mousemove', handleInitialMove);
      document.removeEventListener('mouseup', handleInitialUp);
    };
  }, []);

  // Förhandsvisning av pågående drag för ett kort: { mode, start, end } i
  // minuter på dygnet - exakt de tider som sparas vid släpp (Spår S1) - annars null
  const getDragPreview = (itemId, itemType, item) => {
    if (!dragState.isDragging) return null;
    if (dragState.itemType !== itemType || dragState.itemId !== itemId) return null;
    const deltaY = dragState.currentY - dragState.startY;
    const toMinutes = (d) => d.getHours() * 60 + d.getMinutes();
    const origStart = new Date(item.start_time);
    if (dragState.mode === 'resize') {
      return { mode: 'resize', start: toMinutes(origStart), end: toMinutes(calculateResizedEnd(item, deltaY)) };
    }
    const newStart = calculateMovedStart(item, deltaY);
    const durationMs = new Date(item.end_time).getTime() - origStart.getTime();
    const newEnd = new Date(newStart.getTime() + (durationMs > 0 ? durationMs : 30 * 60000));
    return {
      mode: 'move',
      start: toMinutes(newStart),
      end: toMinutes(newEnd),
      shiftMinutes: (newStart.getTime() - origStart.getTime()) / 60000
    };
  };

  // Vertikal offset under flytt: kortet hoppar i 5-minuterssteg till den tid
  // det hamnar på (vid ändrad längd ligger kortet kvar och växer/krymper)
  const getDragOffset = (preview) => (preview?.mode === 'move'
    ? preview.shiftMinutes * PIXELS_PER_MINUTE
    : 0);

  // Resultattiden som visas på kortet under drag, t.ex. "10:35–11:05"
  const dragPreviewLabel = (preview) => (preview
    ? `${formatMinutes(preview.start)}–${formatMinutes(preview.end)}`
    : null);

  // ==================== END DRAG AND DROP ====================

  // Kontextmeny-åtgärder
  const handleContextMenuAction = (action) => {
    closeContextMenu();

    switch (action) {
      case 'book':
        setSelectedSlot(contextMenu.slotDate);
        setSelectedAppointment(null);
        setSelectedUserIdForBooking(String(contextMenu.userId));
        setShowModal(true);
        break;

      case 'block':
        setSelectedSlot(contextMenu.slotDate);
        setBlockModalMode('block');
        setSelectedBlock(null);
        setSelectedUserIdForBooking(String(contextMenu.userId));
        setShowBlockModal(true);
        break;

      case 'note':
        setSelectedSlot(contextMenu.slotDate);
        setBlockModalMode('note');
        setSelectedBlock(null);
        setSelectedUserIdForBooking(String(contextMenu.userId));
        setShowBlockModal(true);
        break;

      case 'open':
        setSelectedAppointment(contextMenu.appointment);
        setSelectedSlot(null);
        setSelectedUserIdForBooking(String(contextMenu.appointment.user_id));
        setShowModal(true);
        break;

      case 'cancel':
        if (contextMenu.appointment) {
          handleCancelAppointment(contextMenu.appointment);
        }
        break;

      case 'endSeries':
        if (contextMenu.appointment) {
          handleEndSeries(contextMenu.appointment);
        }
        break;

      case 'copy':
        // Kopiera bokning (Spår K4) - klistras in via högerklick på tom tid
        if (contextMenu.appointment) {
          copyAppointment(contextMenu.appointment);
        }
        break;

      case 'paste':
        handlePasteAppointment(contextMenu.slotDate, contextMenu.userId);
        break;

      case 'reschedule':
        setSelectedAppointment(contextMenu.appointment);
        setSelectedSlot(null);
        setSelectedUserIdForBooking(String(contextMenu.appointment.user_id));
        setShowModal(true);
        break;

      case 'bookNew':
        // Boka ny tid för bokningens patient: gör patienten aktiv och öppna
        // "Hitta ledig tid" - modalen förväljer den aktiva patienten
        if (contextMenu.appointment?.patient) {
          setActivePatient?.(contextMenu.appointment.patient);
          setShowSlotSearch(true);
        }
        break;

      case 'payment':
        if (contextMenu.appointment) {
          // is_group-routing (grupp -> deltagarlistan) sköts i handleOpenPaymentModal
          handleOpenPaymentModal(contextMenu.appointment);
        }
        break;

      case 'noShow':
        // Registrera uteblivet: uteblivet-avgift i kassan + röd cell +
        // fakturaunderlag mailas automatiskt (Spår D+E)
        if (contextMenu.appointment) {
          handleRegisterNoShow(contextMenu.appointment);
        }
        break;

      case 'openBlock':
        if (contextMenu.block) {
          setSelectedBlock(contextMenu.block);
          // sick/vacation är block-kategorier - mode måste vara 'block',
          // annars sparar modalen om dem som 'note' (jfr Calendar.js)
          setBlockModalMode(contextMenu.block.block_type === 'note' ? 'note' : 'block');
          setSelectedSlot(new Date(contextMenu.block.start_time));
          setSelectedUserIdForBooking(String(contextMenu.block.user_id));
          setShowBlockModal(true);
        }
        break;

      case 'deleteBlock':
        if (contextMenu.block) {
          handleDeleteBlock(contextMenu.block);
        }
        break;

      case 'openJournal':
        if (contextMenu.appointment?.patient) {
          navigate(`/patients/${contextMenu.appointment.patient.patient_id}?tab=journal`);
        }
        break;

      case 'newJournal':
      case 'startRecording':
        // Båda öppnar patientens journalflik med "Ny journal"-modalen
        // förkopplad till bokningen. JournalTab äger skapandet och öppnar
        // den nya anteckningen i redigering (+ startar dikteringen om
        // "Starta inspelning" valdes) — kalendern har ingen egen modal.
        if (contextMenu.appointment?.patient) {
          const p = contextMenu.appointment.patient;
          // Ett virtuellt tillfälle skickas som referens "<serie>@<datum>" och
          // skapas först när anteckningen sparas (NewJournalModal, Spår R)
          const ref = encodeURIComponent(appointmentKey(contextMenu.appointment));
          navigate(`/patients/${p.patient_id}?tab=journal&ny_journal=1&appointment_id=${ref}`);
        }
        break;

      case 'manageParticipants':
        if (contextMenu.appointment?.is_group) {
          handleOpenGroupParticipants(contextMenu.appointment);
        }
        break;

      case 'bookSlot': {
        const s = contextMenu.scheduleSlot;
        if (s) {
          const [sh, sm] = s.start_time.split(':').map(Number);
          const [eh, em] = s.end_time.split(':').map(Number);
          const slotLen = (eh * 60 + em) - (sh * 60 + sm);
          const slotDate = new Date(currentDate);
          slotDate.setHours(sh, sm, 0, 0);
          setPreSelectedSlotType({
            type_id: s.appointment_type_id,
            name: s.appointment_type?.name,
            duration_minutes: slotLen
          });
          setSelectedSlot(slotDate);
          setSelectedAppointment(null);
          setSelectedUserIdForBooking(String(contextMenu.userId));
          setShowModal(true);
        }
        break;
      }

      case 'addSlot': {
        // Förifyll den klickade tiden (Spår C). slotDate bär timme/minut från klicket.
        const clicked = contextMenu.slotDate;
        setSlotModal({
          mode: 'add',
          date: clicked ? new Date(clicked) : new Date(currentDate),
          userId: contextMenu.userId,
          slot: null,
          defaultStartTime: clicked
            ? `${String(clicked.getHours()).padStart(2, '0')}:${String(clicked.getMinutes()).padStart(2, '0')}`
            : null
        });
        break;
      }

      case 'editSlot':
        if (contextMenu.scheduleSlot) {
          setSlotModal({ mode: 'edit', date: new Date(currentDate), userId: contextMenu.userId, slot: contextMenu.scheduleSlot });
        }
        break;

      case 'removeSlot':
        if (contextMenu.scheduleSlot) {
          handleRemoveSlot(contextMenu.scheduleSlot, contextMenu.userId);
        }
        break;

      default:
        break;
    }
  };

  // Ta bort en schemaslot för dagen
  const handleRemoveSlot = async (slot, userId) => {
    if (!window.confirm('Ta bort schemasloten för denna dag? Mallen påverkas inte.')) return;
    try {
      if (slot.slot_id) {
        await createSlotOverride({
          user_id: userId, date: formatLocalDate(currentDate),
          action: 'remove', source_slot_id: slot.slot_id
        });
      } else if (slot.override_id) {
        await deleteSlotOverride(slot.override_id);
      }
      loadData();
    } catch (err) {
      setError('Kunde inte ta bort schemasloten');
    }
  };

  const handleSlotModalSaved = () => {
    setSlotModal(null);
    loadData();
  };

  // Modal handlers
  const handleOpenPaymentModal = (appointment) => {
    // Gruppbokningar har ingen egen patient - betalning sker per deltagare.
    // Skicka därför vidare till deltagarlistan i stället för individmodalen
    // (annars öppnas betalning för "Ingen patient"). Gäller båda ingångarna:
    // högerklicksmenyn och "Registrera betalning"-knappen i bokningen.
    if (appointment?.is_group) {
      handleOpenGroupParticipants(appointment);
      return;
    }
    setPaymentAppointment(appointment);
    setShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentAppointment(null);
  };

  const handlePaymentSaved = () => {
    handleClosePaymentModal();
    loadData();
  };

  // Registrera uteblivet besök (Spår D+E)
  const handleRegisterNoShow = async (appointment) => {
    if (!window.confirm(await uteblivetBekraftelseText('Registrera bokningen som uteblivet?', appointment.patient_id, appointment.start_time))) return;
    try {
      const real = await ensureRealAppointment(appointment);
      const res = await registerNoShow(real.appointment_id);
      if (res.fakturaunderlag_sent === false) {
        alert(`Uteblivet registrerat, men fakturaunderlaget kunde inte mailas: ${res.fakturaunderlag_error || 'okänt fel'}`);
      }
      loadData();
    } catch (err) {
      console.error('Kunde inte registrera uteblivet:', err);
      alert(err.response?.data?.error || 'Kunde inte registrera uteblivet');
    }
  };

  // Ett virtuellt tillfälle skapas först när kallelsen skickas (KallelseModal)
  const handleOpenKallelseModal = (appointment, preSelectedTemplate = null) => {
    setKallelseAppointment(appointment);
    setPreSelectedKallelseTemplate(preSelectedTemplate);
    setShowKallelseModal(true);
  };

  const handleCloseKallelseModal = () => {
    setShowKallelseModal(false);
    setKallelseAppointment(null);
    setPreSelectedKallelseTemplate(null);
  };

  const handleCloseBlockModal = () => {
    setShowBlockModal(false);
    setSelectedBlock(null);
  };

  const handleBlockSaved = () => {
    handleCloseBlockModal();
    loadData();
  };

  const handleOpenGroupParticipants = (appointment) => {
    setGroupAppointment(appointment);
    setShowGroupModal(true);
  };

  const handleCloseGroupModal = () => {
    setShowGroupModal(false);
    setGroupAppointment(null);
  };

  const handleGroupUpdated = () => {
    loadData();
  };

  const handleSlotSelected = (slotData) => {
    setShowSlotSearch(false);
    const slotDate = new Date(slotData.start_time);
    setCurrentDate(slotDate);
    // Forifyll typ + langd fran sokningen, som vid bokning pa schemaslot
    setPreSelectedSlotType({
      type_id: slotData.appointment_type_id,
      name: slotData.type_name,
      duration_minutes: slotData.duration_minutes
    });
    setSelectedSlot(slotDate);
    setSelectedAppointment(null);
    setSelectedUserIdForBooking(String(slotData.user_id));
    setShowModal(true);
  };

  const handleDeleteBlock = async (block) => {
    const isBlock = block.block_type === 'block';
    const hasRecurrence = block.recurrence_pattern !== 'none' || block.is_recurring_instance;

    let deleteSeries = false;

    if (hasRecurrence) {
      const choice = window.confirm(
        `Vill du ta bort hela serien? (OK = Ta bort alla, Avbryt = Ta bara bort denna ${isBlock ? 'blockering' : 'anteckning'})`
      );
      deleteSeries = choice;
    } else {
      if (!window.confirm(`Är du säker på att du vill ta bort denna ${isBlock ? 'blockering' : 'anteckning'}?`)) {
        return;
      }
    }

    try {
      await deleteBlock(block.block_id, deleteSeries);
      loadData();
    } catch (err) {
      console.error('Error deleting block:', err);
      alert('Kunde inte ta bort');
    }
  };

  const handleBlockClick = (block, e) => {
    e.stopPropagation();
    setSelectedBlock(block);
    // sick/vacation är block-kategorier - mode 'block' (jfr Calendar.js)
    setBlockModalMode(block.block_type === 'note' ? 'note' : 'block');
    setSelectedSlot(new Date(block.start_time));
    setSelectedUserIdForBooking(String(block.user_id));
    setShowBlockModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAppointment(null);
    setSelectedSlot(null);
    setPreSelectedSlotType(null);
  };

  const handleSaveAppointment = () => {
    handleCloseModal();
    loadData();
  };

  // Bokningar tas aldrig bort (Spår K1). En återkommande serie avslutas i
  // stället från och med ett datum: senare tillfällen avbokas/genereras
  // inte, tidigare ligger kvar.
  const handleEndSeries = async (appointment) => {
    // Förslag: klickat tillfälle, men aldrig bakåt i tiden (historiken ligger kvar)
    const today = formatLocalDate(new Date());
    const occ = occurrenceDateOf(appointment);
    const suggested = occ > today ? occ : today;
    const answer = window.prompt(
      'Avsluta serien från och med vilket datum? (ÅÅÅÅ-MM-DD, i dag eller senare)\n\n'
      + 'Tillfällen från och med datumet avbokas och inga nya skapas. '
      + 'Tidigare tillfällen påverkas inte.',
      suggested
    );
    if (answer === null) return;
    const fromDate = answer.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fromDate)) {
      alert('Ange datumet som ÅÅÅÅ-MM-DD');
      return;
    }
    try {
      const res = await endSeries(appointment.appointment_id, fromDate);
      if (res.kept_occurrences) {
        alert(`Serien är avslutad. ${res.kept_occurrences} tillfälle(n) efter datumet är redan `
          + 'genomförda eller betalda och ligger kvar.');
      }
      loadData();
      handleCloseModal();
    } catch (err) {
      console.error('Kunde inte avsluta serien:', err);
      alert(err.response?.data?.error || 'Kunde inte avsluta serien');
    }
  };

  // Klistra in kopierad bokning på en tom tid (Spår K4) via skapa-flödet
  const handlePasteAppointment = async (slotDate, userId) => {
    if (!slotDate || !getCopiedAppointment()) return;
    try {
      const created = await pasteAppointment(slotDate, userId);
      if (created) loadData();
    } catch (err) {
      console.error('Kunde inte klistra in bokningen:', err);
      alert(err.response?.data?.error || 'Kunde inte klistra in bokningen');
    }
  };

  // Avboka - alltid bara detta tillfälle (Spår R: ett virtuellt tillfälle
  // materialiseras först så att inte seriens första tillfälle avbokas)
  const handleCancelAppointment = async (appointment) => {
    const question = isSeriesAppointment(appointment)
      ? 'Avboka detta tillfälle? Övriga tillfällen i serien påverkas inte.'
      : 'Är du säker på att du vill avboka denna tid?';
    if (window.confirm(question)) {
      try {
        const real = await ensureRealAppointment(appointment);
        await cancelAppointment(real.appointment_id);
        loadData();
        handleCloseModal();
      } catch (err) {
        console.error('Error cancelling appointment:', err);
        alert('Kunde inte avboka tiden');
      }
    }
  };

  // Hämta bokningar för en specifik behandlare med positionsberäkning
  // Unified collision detection för alla events (bokningar + blocks)
  const getPositionedEventsForUser = useCallback((userId) => {
    const userAppointments = appointments.filter(apt => apt.user_id === userId);
    // Inkludera både användarens block OCH helgdagar (som gäller alla)
    const userBlocks = blocks.filter(block =>
      block.user_id === userId || block.block_type === 'holiday'
    );

    // Konvertera bokningar till event-objekt
    const aptEvents = userAppointments.map(apt => {
      const start = new Date(apt.start_time);
      const startMinutes = start.getHours() * 60 + start.getMinutes();

      let endMinutes;
      if (apt.end_time) {
        const end = new Date(apt.end_time);
        if (!isNaN(end.getTime())) {
          endMinutes = end.getHours() * 60 + end.getMinutes();
          if (endMinutes < startMinutes && end.getDate() !== start.getDate()) {
            endMinutes += 24 * 60;
          }
        }
      }

      if (!endMinutes || endMinutes <= startMinutes) {
        if (apt.appointment_type?.duration_minutes) {
          endMinutes = startMinutes + apt.appointment_type.duration_minutes;
        } else {
          endMinutes = startMinutes + 30;
        }
      }

      const maxMinutes = 19 * 60;
      if (endMinutes > maxMinutes) {
        endMinutes = maxMinutes;
      }

      return {
        ...apt,
        eventType: 'appointment',
        eventId: apt.appointment_id,
        startMinutes,
        endMinutes,
        column: 0,
        totalColumns: 1
      };
    });

    // Konvertera blocks till event-objekt
    const blockEvents = userBlocks.map(block => {
      const start = new Date(block.start_time);
      const end = new Date(block.end_time);

      const startMinutes = start.getHours() * 60 + start.getMinutes();
      let endMinutes = end.getHours() * 60 + end.getMinutes();
      if (endMinutes <= startMinutes) {
        endMinutes = startMinutes + 60;
      }

      return {
        ...block,
        eventType: 'block',
        eventId: block.block_id,
        startMinutes,
        endMinutes,
        column: 0,
        totalColumns: 1
      };
    });

    // Avbokade bokningar tas UT ur kollisionslayouten och samlas till en liten
    // ikon per starttid (Spår S2) - aktiva bokningar behåller full bredd.
    const activeAptEvents = aptEvents.filter(e => e.status !== 'cancelled');
    const positionedCancelled = groupCancelledByStart(aptEvents.filter(e => e.status === 'cancelled'));

    // Kombinera aktiva bokningar + blocks för kollisionslayout
    const allEvents = [...activeAptEvents, ...blockEvents];

    if (allEvents.length === 0) {
      return { appointments: [], blocks: [], cancelled: positionedCancelled };
    }

    // Sortera efter starttid, längst först vid samma starttid
    allEvents.sort((a, b) => {
      if (a.startMinutes !== b.startMinutes) {
        return a.startMinutes - b.startMinutes;
      }
      return (b.endMinutes - b.startMinutes) - (a.endMinutes - a.startMinutes);
    });

    // Tilldela kolumner
    const columnEndTimes = [];

    allEvents.forEach(event => {
      let assignedColumn = -1;

      for (let col = 0; col < columnEndTimes.length; col++) {
        if (event.startMinutes >= columnEndTimes[col]) {
          assignedColumn = col;
          break;
        }
      }

      if (assignedColumn === -1) {
        assignedColumn = columnEndTimes.length;
        columnEndTimes.push(0);
      }

      event.column = assignedColumn;
      columnEndTimes[assignedColumn] = event.endMinutes;
    });

    // Beräkna totalColumns för varje event baserat på överlappande events
    allEvents.forEach(event => {
      const overlapping = allEvents.filter(other =>
        event.startMinutes < other.endMinutes && event.endMinutes > other.startMinutes
      );
      const maxColumn = Math.max(...overlapping.map(o => o.column));
      event.totalColumns = maxColumn + 1;
    });

    // Lämna plats åt avbokningsikonen i kolumnens högerkant (Spår S2)
    applyCancelledGutter(allEvents, positionedCancelled, PIXELS_PER_MINUTE);

    // Separera tillbaka till bokningar och blocks
    const positionedAppointments = allEvents.filter(e => e.eventType === 'appointment');
    const positionedBlocks = allEvents.filter(e => e.eventType === 'block');

    return { appointments: positionedAppointments, blocks: positionedBlocks, cancelled: positionedCancelled };
  }, [appointments, blocks]);

  // Wrapper-funktioner för bakåtkompatibilitet
  const getPositionedAppointmentsForUser = useCallback((userId) => {
    return getPositionedEventsForUser(userId).appointments;
  }, [getPositionedEventsForUser]);

  const getPositionedBlocksForUser = useCallback((userId) => {
    return getPositionedEventsForUser(userId).blocks;
  }, [getPositionedEventsForUser]);

  const getPositionedCancelledForUser = useCallback((userId) => {
    return getPositionedEventsForUser(userId).cancelled;
  }, [getPositionedEventsForUser]);

  // Hämta schema för en specifik behandlare
  const getScheduleForUser = useCallback((userId) => {
    const dateStr = formatLocalDate(currentDate);
    return scheduleBlocks.find(block => block.date === dateStr && block.user_id === userId);
  }, [scheduleBlocks, currentDate]);

  // Hämta schemamall-slots för en behandlare (dagen)
  const getSlotsForUser = useCallback((userId) => {
    return scheduleSlots[userId] || [];
  }, [scheduleSlots]);

  // Hitta schemaslot som täcker en given tid (vanlig funktion - används i handlers)
  const getSlotAtTime = (userId, hour, minute) => {
    const clickMinutes = hour * 60 + minute;
    return getSlotsForUser(userId).find(s => {
      const [sh, sm] = s.start_time.split(':').map(Number);
      const [eh, em] = s.end_time.split(':').map(Number);
      return clickMinutes >= sh * 60 + sm && clickMinutes < eh * 60 + em;
    }) || null;
  };

  // Formatera datum
  const formatDateHeader = (date) => {
    const weekday = WEEKDAYS[date.getDay()];
    const day = date.getDate();
    const month = date.toLocaleString('sv-SE', { month: 'long' });
    const year = date.getFullYear();
    return `${weekday} ${day} ${month} ${year}`;
  };

  const isToday = currentDate.toDateString() === new Date().toDateString();

  // Hämta valda användare i rätt ordning
  const selectedUsers = clinicUsers.filter(
    u => u.has_calendar === false || selectedUserIds.includes(u.user_id)
  );

  if (loading && appointments.length === 0) {
    return <div className="calendar-loading">Laddar kalender...</div>;
  }

  return (
    <div className="dayview-container">
      {/* Header */}
      <div className="dayview-header">
        <div className="dayview-header-top">
          <div className="dayview-nav">
            <button onClick={goToPreviousWeek} className="nav-btn" title="Föregående vecka">
              <i className="fas fa-angle-double-left"></i>
            </button>
            <button onClick={goToPreviousDay} className="nav-btn" title="Föregående dag">
              <i className="fas fa-chevron-left"></i> Förra dagen
            </button>
            <button onClick={goToToday} className="nav-btn today-btn">Idag</button>
            <button onClick={goToNextDay} className="nav-btn" title="Nästa dag">
              Nästa dag <i className="fas fa-chevron-right"></i>
            </button>
            <button onClick={goToNextWeek} className="nav-btn" title="Nästa vecka">
              <i className="fas fa-angle-double-right"></i>
            </button>
            {/* Spår K2.1: hoppa till valfri dag via månadskalender */}
            <DateJumpPicker selectedDate={currentDate} onSelectDate={setCurrentDate} />
          </div>
          <h2 className={`dayview-title ${isToday ? 'today' : ''}`}>
            {formatDateHeader(currentDate)}
          </h2>
          <div className="header-buttons">
            <button
              className="view-toggle-btn active"
              title="Dagsvy"
            >
              <i className="fas fa-calendar-day"></i>
              Dag
            </button>
            <button
              className="view-toggle-btn"
              onClick={() => onViewChange('week')}
              title="Veckovy"
            >
              <i className="fas fa-calendar-week"></i>
              Vecka
            </button>
            <button
              className="find-time-btn"
              onClick={() => setShowSlotSearch(true)}
              title="Hitta ledig tid"
            >
              <i className="fas fa-search"></i>
              Hitta tid
            </button>
            {/* "Schema" (arbetstider) nås numera via kalenderinställningar (endast admin) */}

            {/* Tidsintervall-väljare */}
            <div className="interval-selector">
              <i className="fas fa-th"></i>
              <select
                value={slotInterval}
                onChange={(e) => setSlotInterval(Number(e.target.value))}
                title="Välj tidsintervall för schemat"
              >
                <option value={60}>60 min</option>
                <option value={30}>30 min</option>
                <option value={15}>15 min</option>
                <option value={10}>10 min</option>
              </select>
            </div>
            <button
              className={`view-toggle-btn compact-toggle-btn ${compact ? 'active' : ''}`}
              onClick={toggleCompact}
              aria-pressed={compact}
              title={compact ? 'Kompakt dagvy är på – klicka för normal bredd' : 'Kompakt: visa alla behandlare utan att scrolla i sidled'}
            >
              <i className="fas fa-compress-alt"></i>
              Kompakt
            </button>
            <CalendarLegend />
          </div>
        </div>

        {/* Behandlar-väljare */}
        <div className="dayview-user-selector">
          <span className="filter-label">
            <i className="fas fa-user-md"></i>
            Behandlare:
          </span>
          <div className="user-selector-actions">
            <button className="select-all-btn" onClick={selectAllUsers}>Alla</button>
            <button className="select-none-btn" onClick={deselectAllUsers}>Ingen</button>
            {/* Sparade vyer (Spår K2) - skapas av admin i Kalenderinställningar */}
            {calendarViews.length > 0 && (
              <select
                className={`view-select ${activeViewId ? 'active' : ''}`}
                value={activeViewId}
                onChange={(e) => handleViewSelect(e.target.value)}
                title="Sparad vy - visar en bestämd grupp behandlare"
                aria-label="Sparad vy"
              >
                <option value="">Vy…</option>
                {calendarViews.map(v => (
                  <option key={v.view_id} value={String(v.view_id)}>{v.name}</option>
                ))}
              </select>
            )}
          </div>
          <div className="user-checkboxes">
            {clinicUsers.map(user => (
              user.has_calendar === false ? (
                // Utan kalender: visas bara dagar med bokningar, alltid med
                <label
                  key={user.user_id}
                  className="user-checkbox calendar-disabled"
                  title="Kalender avstängd - har bokningar denna dag"
                >
                  <input type="checkbox" checked disabled />
                  <span className="user-name">{user.first_name} {user.last_name}</span>
                  <span className="calendar-disabled-tag">Kalender avstängd</span>
                </label>
              ) : (
                <label key={user.user_id} className="user-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedUserIds.includes(user.user_id)}
                    onChange={() => toggleUser(user.user_id)}
                  />
                  <span className="user-name">{user.first_name} {user.last_name}</span>
                </label>
              )
            ))}
          </div>
        </div>
      </div>

      {error && <div className="calendar-error">{error}</div>}

      {selectedUsers.length === 0 ? (
        <div className="dayview-empty">
          <i className="fas fa-user-slash"></i>
          <p>Välj minst en behandlare för att visa schemat</p>
        </div>
      ) : (
        /* Kalenderrutnät */
        <div className={`dayview-grid ${compact ? 'compact' : ''}`} style={{ '--user-count': selectedUsers.length, '--slot-height': `${slotHeight}px` }}>
          {/* Tidkolumn */}
          <div className="time-column">
            <div className="header-cell"></div>
            {timeSlots.map((slot, index) => (
              <div
                key={`${slot.hour}-${slot.minute}`}
                className={`time-cell ${slot.minute !== 0 ? 'sub-hour' : ''}`}
                style={{ height: `${slotHeight}px` }}
              >
                {formatSlotTime(slot)}
              </div>
            ))}
          </div>

          {/* Behandlar-kolumner */}
          {selectedUsers.map((user) => {
            const positionedAppointments = getPositionedAppointmentsForUser(user.user_id);
            const positionedBlocks = getPositionedBlocksForUser(user.user_id);
            const positionedCancelled = getPositionedCancelledForUser(user.user_id);
            const userSchedule = getScheduleForUser(user.user_id);

            // Kontrollera om en slot är inom schema
            const isSlotInSchedule = (slot) => {
              if (!userSchedule || !userSchedule.has_schedule) return false;
              const [scheduleStartHour, scheduleStartMin] = userSchedule.start_time.split(':').map(Number);
              const [scheduleEndHour, scheduleEndMin] = userSchedule.end_time.split(':').map(Number);
              const scheduleStartMinutes = scheduleStartHour * 60 + scheduleStartMin;
              const scheduleEndMinutes = scheduleEndHour * 60 + scheduleEndMin;
              const slotMinutes = slot.hour * 60 + slot.minute;
              return slotMinutes >= scheduleStartMinutes && slotMinutes < scheduleEndMinutes;
            };

            return (
              <div
                key={user.user_id}
                className={`user-column ${user.has_calendar === false ? 'calendar-disabled' : ''}`}
                data-user-id={user.user_id}
              >
                <div className="header-cell user-header" title={`${user.first_name} ${user.last_name}`}>
                  <span className="user-name">{user.first_name} {user.last_name}</span>
                  {user.has_calendar === false && (
                    <span className="calendar-disabled-tag" title="Inga nya bokningar - befintliga kan flyttas eller avbokas">
                      Kalender avstängd
                    </span>
                  )}
                </div>
                <div className="day-slots-container">
                  {/* Nu-linje (Spår 6): bara när vald dag är idag */}
                  {isToday && nowVisible && (
                    <div className="now-line" style={{ top: `${nowTopPx}px` }}>
                      <span className="now-line-dot" />
                    </div>
                  )}
                  {/* Tids-slots för klickbar yta */}
                  {timeSlots.map(slot => {
                    const inSchedule = isSlotInSchedule(slot);
                    const hasSchedule = userSchedule?.has_schedule;
                    const outsideSchedule = hasSchedule && !inSchedule;

                    return (
                      <div
                        key={`${slot.hour}-${slot.minute}`}
                        className={`time-slot ${outsideSchedule ? 'outside-schedule' : ''} ${!hasSchedule ? 'no-schedule-day' : ''} ${slot.minute !== 0 ? 'sub-hour' : ''}`}
                        style={{ height: `${slotHeight}px` }}
                        onClick={() => handleSlotClick(slot, user.user_id)}
                        onDoubleClick={() => handleSlotDoubleClick(slot, user.user_id)}
                        onContextMenu={(e) => handleSlotContextMenu(slot, user.user_id, e)}
                        title={outsideSchedule ? 'Utanför arbetstid' : (!hasSchedule ? 'Ingen arbetstid inlagd' : '')}
                      />
                    );
                  })}

                  {/* Schemamall-slots - tunn remsa till vänster */}
                  {getSlotsForUser(user.user_id).map((s, i) => {
                    const [sh, sm] = s.start_time.split(':').map(Number);
                    const [eh, em] = s.end_time.split(':').map(Number);
                    const slotStart = sh * 60 + sm;
                    const slotEnd = eh * 60 + em;
                    if (slotEnd <= START_HOUR * 60 || slotStart >= END_HOUR * 60) return null;
                    return (
                      <div
                        key={`slot-${user.user_id}-${i}`}
                        className="schedule-slot-strip"
                        style={{
                          top: `${(slotStart - START_HOUR * 60) * PIXELS_PER_MINUTE}px`,
                          height: `${Math.max((slotEnd - slotStart) * PIXELS_PER_MINUTE - 1, 8)}px`,
                          backgroundColor: s.appointment_type?.color || '#6c757d'
                        }}
                        title={`${s.appointment_type?.name || 'Schemaslot'} ${s.start_time}–${s.end_time}`}
                        onClick={(e) => { e.stopPropagation(); setActiveSlotInfo({ x: e.clientX, y: e.clientY, slot: s }); }}
                        onContextMenu={(e) => handleSlotContextMenu({ hour: sh, minute: sm }, user.user_id, e)}
                      />
                    );
                  })}

                  {/* Bokningar */}
                  {positionedAppointments.map(apt => {
                    // Beräkna position baserat på minuter × vertikal skala
                    const topOffset = (apt.startMinutes - START_HOUR * 60) * PIXELS_PER_MINUTE;
                    const aptKey = appointmentKey(apt);
                    // Under drag (Spår S1): kortet hoppar i 5-min-steg, och vid
                    // ändrad längd (Spår K3) växer det till den snappade sluttiden
                    const dragPreview = getDragPreview(aptKey, 'appointment', apt);
                    const resizeEnd = dragPreview?.mode === 'resize' ? dragPreview.end : null;
                    const durationMinutes = (resizeEnd ?? apt.endMinutes) - apt.startMinutes;
                    const height = Math.max(durationMinutes * PIXELS_PER_MINUTE - 2, 20);

                    const colFraction = 1 / apt.totalColumns;
                    const width = `calc(100% * ${colFraction} - ${4 + (apt.cancelledGutter || 0)}px)`;
                    const left = `calc(100% * ${apt.column * colFraction} + 2px)`;

                    const isShort = durationMinutes < 45;
                    const isDragging = dragState.isDragging && dragState.itemType === 'appointment' && dragState.itemId === aptKey;
                    const dragOffset = getDragOffset(dragPreview);
                    // Horisontell preview: kortet följer pekaren i sidled under drag
                    // så man ser att bokningen dras mot en annan behandlare (Spår 7).
                    const dragOffsetX = isDragging && dragState.mode !== 'resize' ? (dragState.currentX - dragState.startX) : 0;
                    const canDrag = canDragAppointment(apt);
                    const isSelected = selectedAppointmentId === aptKey;

                    // Gruppdeltagare (aktiva, dvs ej avbokade) för cell + tooltip (G5)
                    const bookedBy = bookedByText(apt); // Spår K2.2
                    const activeParticipants = apt.is_group && Array.isArray(apt.participants)
                      ? apt.participants.filter(p => p.status !== 'cancelled')
                      : [];
                    const participantCount = activeParticipants.length;

                    // Läsbar textfärg utifrån bakgrunden (skarpare än vit på pastell).
                    // Uteblivna bokningar är röda oavsett typfärg (Spår D).
                    const bgColor = apt.status === 'no_show'
                      ? '#dc3545'
                      : (apt.appointment_type?.color || (apt.is_group ? '#6f42c1' : '#3788d8'));

                    return (
                      <div
                        key={aptKey}
                        className={`appointment appointment-positioned ${apt.status} ${isShort ? 'appointment-short' : ''} ${apt.is_paid ? 'paid' : ''} ${apt.is_group ? 'group-appointment' : ''} ${canDrag ? 'draggable' : 'not-draggable'} ${isDragging ? 'dragging' : ''} ${isSelected ? 'selected' : ''}`}
                        style={{
                          backgroundColor: bgColor,
                          color: textColorForBackground(bgColor),
                          top: `${topOffset + dragOffset}px`,
                          height: `${height}px`,
                          width: width,
                          left: left,
                          transform: isDragging ? `translateX(${dragOffsetX}px) scale(1.02)` : undefined,
                          zIndex: isDragging ? 1000 : undefined,
                          transition: isDragging ? 'none' : undefined,
                          // Under drag: släpp igenom pekaren så elementFromPoint
                          // hittar målkolumnen, inte det dragna kortet (Spår 7)
                          pointerEvents: isDragging ? 'none' : undefined
                        }}
                        onClick={(e) => {
                          if (!isDragging && !justDraggedRef.current) {
                            handleAppointmentClick(apt, e);
                          }
                        }}
                        onDoubleClick={(e) => handleAppointmentDoubleClick(apt, e)}
                        onContextMenu={(e) => handleAppointmentContextMenu(apt, e)}
                        onMouseDown={(e) => handleDragStart(e, 'appointment', apt)}
                      >
                        {/* Låst bokning (Spår K5): förklaring i stället för en ren CSS-prick */}
                        {!canDrag && (
                          <span
                            className="apt-lock-dot"
                            title={apt.is_paid ? 'Betald – kan inte flyttas' : 'Genomförd – kan inte flyttas'}
                            aria-label={apt.is_paid ? 'Betald – kan inte flyttas' : 'Genomförd – kan inte flyttas'}
                          />
                        )}
                        {dragPreview && (
                          <span className="drag-preview-label">{dragPreviewLabel(dragPreview)}</span>
                        )}
                        <div className="appointment-content">
                          {apt.is_paid && <span className="paid-indicator" title="Betald"><i className="fas fa-check-circle"></i></span>}
                          <span className={`appointment-patient${apt.is_group ? ' is-group-row' : ''}`}>
                            {apt.is_group && (
                              <i className="fas fa-users group-inline-icon" title="Gruppbokning"></i>
                            )}
                            <span className="appointment-name-text">
                              {apt.is_group ? apt.group_name : (apt.patient ? apt.patient.fullname : 'Ingen patient')}
                            </span>
                            {apt.is_group && (
                              <span className="group-participant-count" title={`${participantCount} bokade`}>
                                {participantCount}
                              </span>
                            )}
                          </span>
                          {!isShort && (
                            <span className="appointment-time">
                              {new Date(apt.start_time).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                              {apt.appointment_type && ` - ${apt.appointment_type.name}`}
                            </span>
                          )}
                        </div>
                        {/* Hover tooltip */}
                        <div className="appointment-tooltip">
                          <div className="tooltip-header">
                            <strong>{apt.is_group ? apt.group_name : (apt.patient ? apt.patient.fullname : 'Ingen patient')}</strong>
                            {apt.is_group ? (
                              <span className="tooltip-pnr"><i className="fas fa-users"></i> Gruppbokning</span>
                            ) : (
                              apt.patient && <span className="tooltip-pnr">{apt.patient.personnummer}</span>
                            )}
                          </div>
                          <div className="tooltip-row">
                            <i className="fas fa-clock"></i>
                            <span>
                              {new Date(apt.start_time).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                              {' - '}
                              {(() => {
                                const endHour = Math.floor(apt.endMinutes / 60);
                                const endMin = apt.endMinutes % 60;
                                return `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
                              })()}
                            </span>
                          </div>
                          {apt.appointment_type && (
                            <div className="tooltip-row">
                              <i className="fas fa-tag"></i>
                              <span>{apt.appointment_type.name}</span>
                            </div>
                          )}
                          {bookedBy && (
                            <div className="tooltip-row tooltip-booked-by">
                              <i className="fas fa-user-edit"></i>
                              <span>{bookedBy}</span>
                            </div>
                          )}
                          {apt.is_group && (
                            <div className="tooltip-participants">
                              <div className="tooltip-participants-header">
                                <i className="fas fa-users"></i> Deltagare ({participantCount})
                              </div>
                              {participantCount > 0 ? (
                                <ul className="tooltip-participants-list">
                                  {activeParticipants.map(p => (
                                    <li key={p.patient_id}>
                                      {p.patient?.fullname || p.fullname || 'Okänd patient'}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <div className="tooltip-participants-empty">Inga deltagare än</div>
                              )}
                            </div>
                          )}
                          {apt.notes && (
                            <div className="tooltip-notes">
                              <i className="fas fa-sticky-note"></i>
                              <span>{apt.notes}</span>
                            </div>
                          )}
                          <div className="tooltip-status">
                            <span className={`status-badge status-${apt.status}`}>
                              {apt.status === 'scheduled' && 'Bokad'}
                              {apt.status === 'completed' && 'Genomförd'}
                              {apt.status === 'cancelled' && 'Avbokad'}
                              {apt.status === 'no_show' && 'Uteblev'}
                            </span>
                            {apt.is_paid && (
                              <span className="status-badge status-paid">
                                <i className="fas fa-check"></i> Betald ({apt.total_paid} kr)
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Dra i nederkanten för att ändra längd (Spår K3) */}
                        {canDrag && (
                          <div
                            className="resize-handle"
                            title="Dra för att ändra längd"
                            onMouseDown={(e) => handleDragStart(e, 'appointment', apt, 'resize')}
                            onClick={(e) => e.stopPropagation()}
                            onDoubleClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </div>
                    );
                  })}

                  {/* Avbokade bokningar - en liten ikon per starttid (Spår S2) */}
                  {positionedCancelled.map(group => (
                    <CancelledMarker
                      key={`cancelled-${group.startMinutes}`}
                      group={group}
                      top={(group.startMinutes - START_HOUR * 60) * PIXELS_PER_MINUTE}
                      onOpen={handleAppointmentDoubleClick}
                      onContextMenu={handleAppointmentContextMenu}
                    />
                  ))}

                  {/* Blocks/Notes */}
                  {positionedBlocks.map(block => {
                    // Beräkna position baserat på minuter × vertikal skala
                    const topOffset = (block.startMinutes - START_HOUR * 60) * PIXELS_PER_MINUTE;
                    const bKey = blockKey(block);
                    const dragPreview = getDragPreview(bKey, 'block', block);
                    const resizeEnd = dragPreview?.mode === 'resize' ? dragPreview.end : null;
                    const durationMinutes = (resizeEnd ?? block.endMinutes) - block.startMinutes;
                    const height = Math.max(durationMinutes * PIXELS_PER_MINUTE - 2, 20);

                    // Kollisionslayout - samma som bokningar
                    const widthPercent = 100 / block.totalColumns;
                    const leftPercent = widthPercent * block.column;
                    const blockWidth = `calc(${widthPercent}% - ${4 + (block.cancelledGutter || 0)}px)`;
                    const blockLeft = `calc(${leftPercent}% + 2px)`;

                    const isBlock = block.block_type === 'block';
                    const isRecurring = block.recurrence_pattern !== 'none' || block.is_recurring_instance;
                    const isHoliday = block.block_type === 'holiday';
                    const isDraggingBlock = dragState.isDragging && dragState.itemType === 'block' && dragState.itemId === bKey;
                    const blockDragOffset = getDragOffset(dragPreview);

                    // Helgdagar är inte dragbara
                    const isDraggable = !isHoliday;
                    // Längdgrepp bara för block inom en dag (flerdagars, t.ex. semester, redigeras i rutan)
                    const isSingleDay = String(block.start_time).slice(0, 10) === String(block.end_time).slice(0, 10);

                    // Välj ikon baserat på block-typ
                    const getBlockIcon = () => {
                      if (isHoliday) return 'fa-calendar-times';
                      if (isBlock) return 'fa-ban';
                      return 'fa-sticky-note';
                    };

                    return (
                      <div
                        key={`block-${block.block_id}-${block.start_time}`}
                        className={`calendar-block ${block.block_type} ${isRecurring ? 'recurring' : ''} ${isDraggable ? 'draggable' : ''} ${isDraggingBlock ? 'dragging' : ''}`}
                        style={{
                          top: `${topOffset + blockDragOffset}px`,
                          height: `${height}px`,
                          width: blockWidth,
                          left: blockLeft,
                          zIndex: isDraggingBlock ? 1000 : undefined,
                          transition: isDraggingBlock ? 'none' : undefined
                        }}
                        onClick={(e) => {
                          if (!isDraggingBlock && !justDraggedRef.current && !isHoliday) {
                            handleBlockClick(block, e);
                          }
                        }}
                        onContextMenu={(e) => !isHoliday && handleBlockContextMenu(block, e)}
                        onMouseDown={(e) => isDraggable && handleDragStart(e, 'block', block)}
                      >
                        {dragPreview && (
                          <span className="drag-preview-label">{dragPreviewLabel(dragPreview)}</span>
                        )}
                        <div className="block-content">
                          <span
                            className="block-icon"
                            title={isHoliday ? 'Röd dag – går inte att boka' : (block.blocks_booking ? 'Blockerar bokning' : 'Går att boka på tiden')}
                          >
                            <i className={`fas ${getBlockIcon()}`}></i>
                          </span>
                          <span className="block-title">{block.title}</span>
                          {isRecurring && (
                            <span className="block-recurring" title="Återkommande">
                              <i className="fas fa-redo"></i>
                            </span>
                          )}
                        </div>
                        <div className="block-tooltip">
                          <div className="tooltip-header">
                            <i className={`fas ${getBlockIcon()}`}></i>
                            <strong>{block.title}</strong>
                          </div>
                          {isHoliday ? (
                            <div className="tooltip-row">
                              <i className="fas fa-calendar-day"></i>
                              <span>Heldag</span>
                            </div>
                          ) : (
                            <div className="tooltip-row">
                              <i className="fas fa-clock"></i>
                              <span>
                                {new Date(block.start_time).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                {' - '}
                                {new Date(block.end_time).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          )}
                          {isRecurring && (
                            <div className="tooltip-row">
                              <i className="fas fa-redo"></i>
                              <span>
                                {block.recurrence_pattern === 'daily' && 'Varje dag'}
                                {block.recurrence_pattern === 'weekly' && 'Varje vecka'}
                                {block.recurrence_pattern === 'biweekly' && 'Varannan vecka'}
                              </span>
                            </div>
                          )}
                          <div className={`tooltip-type ${block.block_type}`}>
                            {isHoliday ? 'Röd dag - alla kalendrar blockerade' : (isBlock ? 'Blockerar bokning' : 'Endast anteckning')}
                          </div>
                        </div>
                        {isDraggable && isSingleDay && (
                          <div
                            className="resize-handle"
                            title="Dra för att ändra längd"
                            onMouseDown={(e) => handleDragStart(e, 'block', block, 'resize')}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Högerklicksmeny */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className="calendar-context-menu"
          style={{
            position: 'fixed',
            top: contextMenuPos.top,
            left: contextMenuPos.left,
            zIndex: 10000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.appointment ? (
            <>
              <div className="context-menu-item" onClick={() => handleContextMenuAction('open')}>
                <i className="fas fa-folder-open"></i>
                <span>Öppna bokning</span>
              </div>
              <div className="context-menu-item" onClick={() => handleContextMenuAction('reschedule')}>
                <i className="fas fa-calendar-alt"></i>
                <span>Omboka tid</span>
              </div>
              {contextMenu.appointment.patient && (
                <div className="context-menu-item" onClick={() => handleContextMenuAction('bookNew')}>
                  <i className="fas fa-calendar-plus"></i>
                  <span>Boka ny tid</span>
                </div>
              )}
              <div className="context-menu-item" onClick={() => handleContextMenuAction('copy')}>
                <i className="fas fa-copy"></i>
                <span>Kopiera</span>
              </div>
              {contextMenu.appointment.status !== 'cancelled' && (
                <div className="context-menu-item context-menu-item-danger" onClick={() => handleContextMenuAction('cancel')}>
                  <i className="fas fa-times-circle"></i>
                  <span>Avboka tid</span>
                </div>
              )}
              {/* Bokningar tas aldrig bort (Spår K1) - en serie avslutas i stället */}
              {isSeriesAppointment(contextMenu.appointment) && (
                <div className="context-menu-item context-menu-item-danger" onClick={() => handleContextMenuAction('endSeries')}>
                  <i className="fas fa-calendar-times"></i>
                  <span>Avsluta serien från och med…</span>
                </div>
              )}
              {!contextMenu.appointment.is_paid && contextMenu.appointment.status !== 'cancelled' && (
                <div className="context-menu-item context-menu-item-success" onClick={() => handleContextMenuAction('payment')}>
                  <i className="fas fa-cash-register"></i>
                  <span>Registrera betalning</span>
                </div>
              )}
              {contextMenu.appointment.is_paid && (
                <div className="context-menu-item context-menu-item-info">
                  <i className="fas fa-check-circle"></i>
                  <span>Betald ({contextMenu.appointment.total_paid} kr)</span>
                </div>
              )}
              {/* Uteblivet: avgift i kassan + röd cell + fakturaunderlag (gruppers deltagare hanteras i deltagarlistan) */}
              {!contextMenu.appointment.is_group && !contextMenu.appointment.is_paid &&
                !['cancelled', 'no_show'].includes(contextMenu.appointment.status) && (
                <div className="context-menu-item context-menu-item-danger" onClick={() => handleContextMenuAction('noShow')}>
                  <i className="fas fa-user-slash"></i>
                  <span>Registrera som uteblivet</span>
                </div>
              )}
              {contextMenu.appointment.is_group && (
                <div className="context-menu-item" onClick={() => handleContextMenuAction('manageParticipants')}>
                  <i className="fas fa-users"></i>
                  <span>Hantera deltagare</span>
                </div>
              )}
              {contextMenu.appointment.patient && useInternalJournal && (
                <>
                  <div className="context-menu-divider"></div>
                  <div className="context-menu-item" onClick={() => handleContextMenuAction('openJournal')}>
                    <i className="fas fa-file-medical"></i>
                    <span>Öppna journal</span>
                  </div>
                  <div className="context-menu-item" onClick={() => handleContextMenuAction('newJournal')}>
                    <i className="fas fa-file-medical-alt"></i>
                    <span>Ny journal</span>
                  </div>
                  <div className="context-menu-item" onClick={() => handleContextMenuAction('startRecording')}>
                    <i className="fas fa-microphone"></i>
                    <span>Starta inspelning</span>
                  </div>
                </>
              )}
              <div className="context-menu-divider"></div>
            </>
          ) : null}

          {contextMenu.block ? (
            <>
              <div className="context-menu-item" onClick={() => handleContextMenuAction('openBlock')}>
                <i className={`fas ${contextMenu.block.block_type === 'block' ? 'fa-ban' : 'fa-sticky-note'}`}></i>
                <span>Redigera {contextMenu.block.block_type === 'block' ? 'blockering' : 'anteckning'}</span>
              </div>
              <div className="context-menu-item context-menu-item-danger" onClick={() => handleContextMenuAction('deleteBlock')}>
                <i className="fas fa-trash"></i>
                <span>Ta bort {contextMenu.block.block_type === 'block' ? 'blockering' : 'anteckning'}</span>
              </div>
              <div className="context-menu-divider"></div>
            </>
          ) : null}

          {/* Schemaslot-alternativ (endast utan bokning/block) */}
          {!contextMenu.appointment && !contextMenu.block && (
            contextMenu.scheduleSlot ? (
              <>
                <div className="context-menu-item context-menu-item-success" onClick={() => handleContextMenuAction('bookSlot')}>
                  <i className="fas fa-user-plus"></i>
                  <span>
                    Boka {contextMenu.scheduleSlot.appointment_type?.name || 'på slot'}
                    {' '}({contextMenu.scheduleSlot.start_time}–{contextMenu.scheduleSlot.end_time})
                  </span>
                </div>
                <div className="context-menu-item" onClick={() => handleContextMenuAction('editSlot')}>
                  <i className="fas fa-edit"></i>
                  <span>Ändra schemaslot</span>
                </div>
                <div className="context-menu-item context-menu-item-danger" onClick={() => handleContextMenuAction('removeSlot')}>
                  <i className="fas fa-calendar-minus"></i>
                  <span>Ta bort schemaslot</span>
                </div>
                <div className="context-menu-divider"></div>
              </>
            ) : (
              <>
                <div className="context-menu-item" onClick={() => handleContextMenuAction('addSlot')}>
                  <i className="fas fa-calendar-plus"></i>
                  <span>Lägg schemaslot här</span>
                </div>
                <div className="context-menu-divider"></div>
              </>
            )
          )}

          {/* Klistra in (Spår K4) - bara på tom tid och när något är kopierat */}
          {!contextMenu.appointment && !contextMenu.block && getCopiedAppointment() && (
            <div className="context-menu-item context-menu-item-success" onClick={() => handleContextMenuAction('paste')}>
              <i className="fas fa-paste"></i>
              <span>Klistra in: {copiedLabel()}</span>
            </div>
          )}
          <div className="context-menu-item" onClick={() => handleContextMenuAction('book')}>
            <i className="fas fa-user-plus"></i>
            <span>Boka patient</span>
          </div>
          <div className="context-menu-item" onClick={() => handleContextMenuAction('block')}>
            <i className="fas fa-ban"></i>
            <span>Blocka tid</span>
          </div>
          <div className="context-menu-item" onClick={() => handleContextMenuAction('note')}>
            <i className="fas fa-sticky-note"></i>
            <span>Lägg till anteckning</span>
          </div>
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <AppointmentModal
          appointment={selectedAppointment}
          initialDate={selectedSlot}
          appointmentTypes={appointmentTypes}
          selectedUserId={selectedUserIdForBooking}
          clinicUsers={clinicUsers}
          preSelectedType={preSelectedSlotType}
          onClose={handleCloseModal}
          onSave={handleSaveAppointment}
          onEndSeries={handleEndSeries}
          onCancel={handleCancelAppointment}
          onOpenPaymentModal={handleOpenPaymentModal}
          onOpenKallelseModal={handleOpenKallelseModal}
          onOpenGroupParticipants={handleOpenGroupParticipants}
        />
      )}

      {slotModal && (
        <SlotOverrideModal
          mode={slotModal.mode}
          date={slotModal.date}
          userId={slotModal.userId}
          slot={slotModal.slot}
          defaultStartTime={slotModal.defaultStartTime}
          appointmentTypes={appointmentTypes}
          onClose={() => setSlotModal(null)}
          onSaved={handleSlotModalSaved}
        />
      )}

      {activeSlotInfo && (
        <>
          <div className="schedule-slot-info-backdrop" onClick={() => setActiveSlotInfo(null)} />
          <div
            className="schedule-slot-info"
            style={{ top: `${activeSlotInfo.y + 10}px`, left: `${activeSlotInfo.x + 10}px` }}
          >
            <div className="ssi-header">
              <span className="ssi-dot" style={{ backgroundColor: activeSlotInfo.slot.appointment_type?.color || '#6c757d' }} />
              <strong>{activeSlotInfo.slot.appointment_type?.name || 'Schemaslot'}</strong>
            </div>
            <div className="ssi-time">
              <i className="fas fa-clock"></i> {activeSlotInfo.slot.start_time}–{activeSlotInfo.slot.end_time}
            </div>
            {activeSlotInfo.slot.template_name && (
              <div className="ssi-template">
                <i className="fas fa-calendar-week"></i> {activeSlotInfo.slot.template_name}
              </div>
            )}
          </div>
        </>
      )}

      {showPaymentModal && paymentAppointment && (
        <PaymentModal
          appointment={paymentAppointment}
          onClose={handleClosePaymentModal}
          onSave={handlePaymentSaved}
        />
      )}

      {showKallelseModal && kallelseAppointment && (
        <KallelseModal
          appointment={kallelseAppointment}
          preSelectedTemplate={preSelectedKallelseTemplate}
          onClose={handleCloseKallelseModal}
        />
      )}

      {showBlockModal && (
        <BlockNoteModal
          mode={blockModalMode}
          initialDate={selectedSlot}
          existingBlock={selectedBlock}
          selectedUserId={selectedUserIdForBooking}
          clinicUsers={clinicUsers}
          onClose={handleCloseBlockModal}
          onSave={handleBlockSaved}
        />
      )}


      {showGroupModal && groupAppointment && (
        <GroupParticipantsModal
          appointment={groupAppointment}
          onClose={handleCloseGroupModal}
          onUpdate={handleGroupUpdated}
          onOpenPatient={(patient) => {
            handleCloseGroupModal();
            navigate(`/patients/${patient.patient_id}?tab=journal`);
          }}
        />
      )}

      {showSlotSearch && (
        <AvailableSlotSearch
          onSelectSlot={handleSlotSelected}
          onClose={() => setShowSlotSearch(false)}
        />
      )}
    </div>
  );
};

export default DayView;
